/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { jsonWebKeySetsActivateKey } from "../funcs/jsonWebKeySetsActivateKey.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { JSONWebKey } from "../models/components/jsonwebkey.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ActivateJsonWebKeyRequest,
  ActivateJsonWebKeySecurity,
} from "../models/operations/activatejsonwebkey.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type ActivateJsonWebKeyMutationVariables = {
  request: ActivateJsonWebKeyRequest;
  security?: ActivateJsonWebKeySecurity | undefined;
  options?: RequestOptions;
};

export type ActivateJsonWebKeyMutationData = JSONWebKey;

export type ActivateJsonWebKeyMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * activateKey jsonWebKeySets
 *
 * @remarks
 * Make a published key the set's active signing key, retiring the previously active key in the same operation. The key must be pending or retired; activating the already-active key is a no-op. Requires org:admin.
 */
export function useActivateJsonWebKeyMutation(
  options?: MutationHookOptions<
    ActivateJsonWebKeyMutationData,
    ActivateJsonWebKeyMutationError,
    ActivateJsonWebKeyMutationVariables
  >,
): UseMutationResult<
  ActivateJsonWebKeyMutationData,
  ActivateJsonWebKeyMutationError,
  ActivateJsonWebKeyMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildActivateJsonWebKeyMutation(client, options),
    ...options,
  });
}

export function mutationKeyActivateJsonWebKey(): MutationKey {
  return ["@gram/client", "jsonWebKeySets", "activateKey"];
}

export function buildActivateJsonWebKeyMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: ActivateJsonWebKeyMutationVariables,
  ) => Promise<ActivateJsonWebKeyMutationData>;
} {
  return {
    mutationKey: mutationKeyActivateJsonWebKey(),
    mutationFn: function activateJsonWebKeyMutationFn({
      request,
      security,
      options,
    }): Promise<ActivateJsonWebKeyMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(jsonWebKeySetsActivateKey(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
